<template>
  <MobileModal v-model="cartIsShow"
               :dialog="isCartFixed"
               animation-type="collapse">
    <LoadingSpinner v-if="isLoading"/>
    <MobileContainer>
      <MobileHeader slot="header"
                    nav-action="collapse"
                    show-cart
                    cart-disabled
                    @collapse="collapseCart">
        My cart
      </MobileHeader>
      <div :class="$style.cartContent">
        <CartProduct v-for="(value, key) in cart"
                     :key="key"
                     :order-item-id="key"
                     :class="$style.currentOrderItem"
                     @change-quantity="changeQuantity(key, $event)"
                     @edit="handleEditItem"/>
      </div>
      <CartTotal slot="footer"
                 :current-page="currentPage"
                 @add-item="addNewItem"
                 @start-over="handleStartOver"
                 @checkout="checkout"/>
    </MobileContainer>

    <!-- Modal remove item -->
    <MobileModal v-model="showRemoveItemConfirm"
                 dialog
                 center-content>
      <MobileConfirmDialog title="Remove Item"
                           cancel-text="Cancel"
                           confirm-text="Remove"
                           @confirm="confirmRemoveItem()"
                           @cancel="cancelRemoveItem()">
        Do you want to remove this item from your order?
      </MobileConfirmDialog>
    </MobileModal>
    <!-- / Modal remove item -->
    <!-- Modal cancel order -->
    <MobileModal v-model="showCancelOrderConfirm"
                 dialog
                 center-content>
      <MobileConfirmDialog title="Cancel Order"
                           cancel-text="No"
                           confirm-text="Yes, cancel"
                           @confirm="confirmCancelOrder()"
                           @cancel="rejectCancelOrder()">
        Are you sure you want to cancel your entire order?
      </MobileConfirmDialog>
    </MobileModal>
    <!-- / Modal cancel order -->

    <!-- Modal confirm abandon this creation -->
    <MobileModal v-model="showAbandonCreationDialog"
                 dialog
                 center-content>
      <MobileConfirmDialog title="Abandon Boss Creation?"
                           cancel-text="No"
                           confirm-text="Yes, Abandon"
                           @confirm="confirmAbandonCreation()"
                           @cancel="cancelAbandonCreation()">
        Are you sure you want to abandon this creation?
      </MobileConfirmDialog>
    </MobileModal>
    <!-- / Modal confirm abandon this creation -->
  </MobileModal>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import MobileContainer from '@/apps/mobile/components/MobileContainer';
import MobileHeader from '@/apps/mobile/components/MobileHeader';
import MobileButton from '@/apps/mobile/components/MobileButton';
import CartProduct from '@/apps/cart/components/CartProduct';
import CartTotal from '@/apps/cart/components/CartTotal';
import MobileModal from '@/apps/mobile/components/MobileModal';
import MobileConfirmDialog from '@/apps/mobile/components/MobileConfirmDialog';
import { Getter, Action } from 'vuex-class';
import LoadingMixin from '@/lib/mixins/loading-mixin';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';
import { API_ERROR_SET } from '@/apps/core/store/types';
import {
  CART_COLLAPSE,
  CART_ITEM_SET_QUANTITY,
  ORDER_CREATE,
  CART_ITEM_REMOVE,
  CART_PREPARE_ORDER,
  CART_RESET,
  CART_CALC_ORDER,
  ORDER_PATCH,
} from '@/apps/cart/store/types';
import {
  CONFIGURATOR_CLEAR,
  CONFIGURATOR_PRODUCT,
} from '@/apps/configurator/store/types';
import { debounce } from 'debounce';
import { DEBOUNCE_ORDER_QUANTITY_CALC } from '@/config/constants';


/**
 * Mobile cart component.
 * List of products in cart.
 */
@Component({
  components: {
    LoadingSpinner,
    MobileButton,
    MobileHeader,
    MobileContainer,
    CartProduct,
    CartTotal,
    MobileModal,
    MobileConfirmDialog,
  },
  mixins: [
    LoadingMixin,
  ],
  props: {
    currentPage: '',
  },
})
export default class MobileCart extends Vue {
  @Getter cart;
  @Getter authUser;
  @Getter orderItemPrice;
  @Getter restaurantId;
  @Getter orderType;
  @Getter cartLength;
  @Getter orderItemId;
  @Getter orderItemCompleted;
  @Getter showCart;
  @Getter cartIsEmpty;
  @Getter order;
  @Getter getPreparedItems;
  @Action(CART_ITEM_SET_QUANTITY) cartItemSetQuantity;
  @Action(CART_ITEM_REMOVE) cartItemRemove;
  @Action(CONFIGURATOR_PRODUCT) setCurrentProduct;
  @Action(ORDER_CREATE) orderCreate;
  @Action(CART_COLLAPSE) cartCollapse;
  @Action(CART_PREPARE_ORDER) prepareOrder;
  @Action(CART_RESET) clearCart;
  @Action(CONFIGURATOR_CLEAR) clearConfigurator;
  @Action(CART_CALC_ORDER) cartCalcOrder;
  @Action(API_ERROR_SET) setApiError;
  @Action(ORDER_PATCH) updateOrder;

  /**
   * Show abandon creation.
   * @type {boolean}
   */
  showAbandonCreationDialog = false;

  /**
   *  Show remove item confirm dialog.
   *  @type {Boolean}
   */
  showRemoveItemConfirm = false;

  /**
   * Show cancel order confirm dialog.
   * @type {Boolean}
   */
  showCancelOrderConfirm = false;

  /**
   * Changed item id.
   * @type {String}
   */
  lastChangedItemId = null;

  /**
   * Fixed cart.
   * Canceling close on back button.
   */
  get isCartFixed() {
    return this.showCancelOrderConfirm
      || this.showAbandonCreationDialog
      || this.showRemoveItemConfirm;
  }

  /**
   * Cart is show getter.
   * @return {Boolean}.
   */
  get cartIsShow() {
    return this.showCart;
  }
  /**
   * Cart is show setter.
   * @param {Boolean} value.
   */
  set cartIsShow(value) {
    if (!value) {
      this.collapseCart();
    }
  }

  created() {
    this.calcOrder = debounce(this.calcOrder, DEBOUNCE_ORDER_QUANTITY_CALC);
  }

  /**
   * Collapse cart.
   */
  collapseCart() {
    this.cartCollapse();
  }

  /**
   * Handler tothis.idItemForRemove change the quantity of items in the Cart.
   *
   * If you try to reduce the quantity to zero, displays a confirmation modal
   */
  changeQuantity(itemId, quantity) {
    this.lastChangedItemId = itemId;

    if (quantity === 0) {
      this.showRemoveItemConfirm = true;
    } else {
      this.cartItemSetQuantity({
        itemId,
        quantity,
      });

      this.calcOrder();
    }
  }

  /**
   * Calc order with debounce.
   */
  async calcOrder() {
    try {
      await this.cartCalcOrder();
    } catch (error) {
      this.setApiError({ detail: error.response.data.detail });
      throw new Error(error);
    }
  }

  /**
   * Confirm remove item.
   */
  confirmRemoveItem() {
    this.cartItemRemove(this.lastChangedItemId);
    this.showRemoveItemConfirm = false;
    if (this.orderItemId === this.lastChangedItemId) {
      this.addNewItem();
    }

    if (this.cartIsEmpty) {
      this.clearCart();
    } else {
      this.cartCalcOrder();
    }
  }

  /**
   * Cancel remove item.
   */
  cancelRemoveItem() {
    this.showRemoveItemConfirm = false;
  }

  /**
   * Confirm cancel order.
   */
  confirmCancelOrder() {
    this.clearCart();
    this.showCancelOrderConfirm = false;
    this.goToMenu();
    this.cartCollapse();
  }

  /**
   * Reject cancel order.
   */
  rejectCancelOrder() {
    this.showCancelOrderConfirm = false;
  }

  /**
   * Confirm abandon creation.
   */
  confirmAbandonCreation() {
    this.createItemEdit();
    this.showAbandonCreationDialog = false;
  }

  /**
   * Cancel abandon creation.
   */
  cancelAbandonCreation() {
    this.showAbandonCreationDialog = false;
  }

  /**
   * Handler edit item.
   * @param {String} itemId - item id in cart.
   */
  handleEditItem(itemId) {
    const currentItem = itemId === this.orderItemId;

    this.lastChangedItemId = itemId;

    if (currentItem) {
      this.cartCollapse();

      return;
    }

    if (!this.orderItemCompleted) {
      this.showAbandonCreationDialog = true;
    } else {
      this.createItemEdit();
    }
  }

  /**
   * Create item edit.
   */
  createItemEdit() {
    const itemId = this.lastChangedItemId;
    const item = this.cart[itemId];

    if (!this.orderItemCompleted) {
      this.cartItemRemove(this.orderItemId);
    }

    this.clearConfigurator();

    this.setCurrentProduct({
      productKey:  item.productKey,
      orderItemId: itemId,
      orderItem:   item.data,
    });

    this.$router.push({ name: 'configurator', params: { id: item.productKey } });
    this.cartCollapse();
  }

  /**
   * Redirect to menu, for adding new item to card
   */
  addNewItem() {
    this.cartCollapse();
    this.goToMenu();
  }

  /**
   * Handle "Start Over" event
   */
  handleStartOver() {
    if (this.cartLength > 0) {
      this.showCancelOrderConfirm = true;
    } else {
      this.goToMenu();
      this.cartCollapse();
    }
  }

  /**
   * Go to menu.
   */
  goToMenu() {
    this.$router.push({ name: 'menu' });
  }

  /**
   * Checkout action.
   */
  async checkout(payload) {
    try {
      this.loadingStart();
      if (payload.promoCode) {
        await this.updateOrder({
          promoCode: payload.promoCode,
          items:     this.getPreparedItems,
        });
        this.$router.push({ name: 'payment-confirm' });
      } else if (this.order) {
        await this.updateOrder({ items: this.getPreparedItems });
        this.$router.push({ name: 'payment-confirm' });
      } else {
        await this.prepareOrder();
        this.$router.push({ name: 'payment-confirm' });
      }
    } catch (e) {
      this.$router.push({ name: 'menu' });
      throw e;
    } finally {
      this.loadingStop();
      this.cartCollapse();
    }
  }
}
</script>

<style lang="scss" module>
@import '@/style/variables.scss';

.cartContent {
  background: $mobile-cart-bg;
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 10px;
  overflow: inherit;
}

</style>



// WEBPACK FOOTER //
// src/apps/mobile/components/MobileCart.vue